import {
  SUMMARY_LANGUAGE_DEFAULT,
  SUMMARY_LANGUAGES,
  type SummaryLanguage,
} from "@rosc/contracts";

export { SUMMARY_LANGUAGE_DEFAULT, SUMMARY_LANGUAGES, type SummaryLanguage };

// The patient summary language last chosen, remembered in this browser per
// account, like the reading level (lib/reading-level.ts). Nothing saved: the
// account's note language when it is one of the summary languages, so a
// French-note account keeps French summaries, else English.
const KEY = "rosc:summaryLanguage";
type Account = string | null | undefined;
const keyFor = (account: Account): string =>
  account ? `${KEY}:${account}` : KEY;

export const isSummaryLanguage = (value: unknown): value is SummaryLanguage =>
  SUMMARY_LANGUAGES.some((language) => language === value);

// The account's note language (ISO 639-3, Settings → Transcription) as a
// summary language. Chinese notes have been written in Simplified script.
const NOTE_LANGUAGES: Record<string, SummaryLanguage> = {
  eng: "en",
  fra: "fr",
  spa: "es",
  por: "pt",
  jpn: "ja",
  zho: "zh-Hans",
  cmn: "zh-Hans",
};
export const summaryLanguageForNote = (
  code: string | null | undefined,
): SummaryLanguage =>
  NOTE_LANGUAGES[code?.trim().toLowerCase() ?? ""] ?? SUMMARY_LANGUAGE_DEFAULT;

export const readSummaryLanguage = (
  account: Account,
  noteLanguage?: string | null,
): SummaryLanguage => {
  const fallback = summaryLanguageForNote(noteLanguage);
  try {
    const saved = window.localStorage.getItem(keyFor(account));
    return isSummaryLanguage(saved) ? saved : fallback;
  } catch {
    return fallback;
  }
};

export const rememberSummaryLanguage = (
  account: Account,
  language: SummaryLanguage,
): void => {
  try {
    window.localStorage.setItem(keyFor(account), language);
  } catch {
    // A convenience only.
  }
};

// Each language in its own words, shown next to the interface-language name
// so the clinician and the patient both recognise it.
export const SUMMARY_LANGUAGE_NATIVE: Record<SummaryLanguage, string> = {
  en: "English",
  "zh-Hans": "简体中文",
  "zh-Hant": "繁體中文",
  pt: "Português",
  fr: "Français",
  ja: "日本語",
  es: "Español",
};

// The language a saved summary is written in, read from its text (the
// language is not stored with the note). Null when unsure: no label then.
// Chinese script by characters that differ between Simplified and
// Traditional; Japanese by kana; Latin languages by common short words.
const SIMPLIFIED = new Set(
  "们这会时说为个来药医压检体后开关对与际当间还从现进长种处应头诊疗验嘱复岁须让请号肾脏带计剂随访吗两钟数测异状况点觉经给么过动发问题够议纪录记变换据边级缓饮觉见视让认识语调谈软轻较选释钙铁维钠钾养营湿热冻伤疮痒肤肠胆脑颈儿妇产网线络单双买卖费险",
);
const TRADITIONAL = new Set(
  "們這會時說為個來藥醫壓檢體後開關對與際當間還從現進長種處應頭診療驗囑復歲須讓請號腎臟帶計劑隨訪嗎兩鐘數測異狀況點覺經給麼過動發問題夠議紀錄記變換據邊級緩飲覺見視讓認識語調談軟輕較選釋鈣鐵維鈉鉀養營濕熱凍傷瘡癢膚腸膽腦頸兒婦產網線絡單雙買賣費險",
);
const KANA = /[぀-ヿ]/gu;
const HAN = /[一-鿿]/gu;
const LATIN_WORDS: Record<"en" | "pt" | "fr" | "es", readonly string[]> = {
  en: [
    "the",
    "and",
    "your",
    "you",
    "to",
    "of",
    "with",
    "for",
    "is",
    "this",
    "take",
    "are",
    "we",
    "will",
    "in",
  ],
  pt: [
    "você",
    "não",
    "seu",
    "sua",
    "com",
    "uma",
    "do",
    "da",
    "os",
    "ao",
    "é",
    "são",
    "também",
    "dia",
    "pressão",
    "vezes",
  ],
  fr: [
    "le",
    "les",
    "des",
    "du",
    "et",
    "votre",
    "vous",
    "pour",
    "avec",
    "une",
    "est",
    "pas",
    "dans",
    "au",
    "sur",
    "jour",
  ],
  es: [
    "el",
    "los",
    "las",
    "del",
    "y",
    "su",
    "usted",
    "con",
    "una",
    "es",
    "día",
    "también",
    "al",
    "presión",
    "veces",
  ],
};

export const detectSummaryLanguage = (
  text: string | null | undefined,
): SummaryLanguage | null => {
  if (!text?.trim()) return null;
  const kana = text.match(KANA)?.length ?? 0;
  const han = text.match(HAN) ?? [];
  if (kana >= 5) return "ja";
  if (han.length >= 10) {
    let simplified = 0;
    let traditional = 0;
    for (const char of han) {
      if (SIMPLIFIED.has(char)) simplified += 1;
      else if (TRADITIONAL.has(char)) traditional += 1;
    }
    if (simplified > traditional * 3 && simplified >= 2) return "zh-Hans";
    if (traditional > simplified * 3 && traditional >= 2) return "zh-Hant";
    return null;
  }
  const words = text.toLowerCase().match(/\p{L}+/gu) ?? [];
  const scores = (
    Object.entries(LATIN_WORDS) as [
      keyof typeof LATIN_WORDS,
      readonly string[],
    ][]
  )
    .map(([language, list]) => ({
      language,
      score: words.filter((word) => list.includes(word)).length,
    }))
    .sort((a, b) => b.score - a.score);
  const [first, second] = scores;
  if (!first || first.score < 5) return null;
  return first.score >= (second?.score ?? 0) * 2 ? first.language : null;
};
